import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { OrderingDirections } from 'generated/graphql-types';
import { EMPTY, of } from 'rxjs';
import { catchError, map, switchMap, switchMapTo, withLatestFrom } from 'rxjs/operators';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { AddProviderCallCostComponent } from '../../add-provider-call-costs/containers/add-provider-call-costs.component';
import { CallCostsFilterOption } from '../models/call-cost-filter-option';
import { CallCostOrderingOptions, CallCostRegisterOptions } from '../models/call-cost-register-options';
import { CallCostRegisterListItem, ScheduleRegisterResults } from '../models/call-cost-register-results.model';
import {
   GetProviderCallRate,
   GetProviderCallRate_providerCallRatesConnection_items,
} from '../queries/generated/GetProviderCallRate';
import { CallCostRegisterApiService } from '../services/call-cost-register-api.service';
import { addProviderCallRateFile, callCostRegisterAdapterActions } from './call-cost-register.actions';
import {
   CallCostRegisterState,
   selectEndCursor,
   selectOrderingOptions,
   selectQuickFilters,
} from './call-cost-register.reducer';
import * as CallCostsRegisterSelectors from './call-cost-register.selectors';
import { selectFilters } from './call-cost-register.selectors';

@Injectable()
export class CallCostRegisterEffects {
   loadCallCostsList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(callCostRegisterAdapterActions.pageEnter),
         withLatestFrom(
            this.store.pipe(select(CallCostsRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([, filters, quickFilters, orderingOptions]) => {
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   loadcallCostPage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(callCostRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(CallCostsRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, endCursor, quickFilters, orderingOptions]) => {
            return this.loadNewPage(endCursor, filters, quickFilters, orderingOptions);
         })
      );
   });

   quickFilterChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(callCostRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(CallCostsRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, quickFilters, filters, orderingOptions]) => {
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });
   orderingOptionsChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(callCostRegisterAdapterActions.orderingOptionsChanged),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, quickfilters, orderingOptions]) => {
            return this.loadNewData(filters, quickfilters, orderingOptions);
         })
      );
   });

   addProviderCallRateFile$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(addProviderCallRateFile),
            switchMap((providerCallRates) => {
               const dialogRef = this.dialogService.openFormComponent(AddProviderCallCostComponent, {
                  data: null,
                  autoFocus: false,
                  disableClose: true,
                  panelClass: ['add-dialog-panel', 'dialog-container'],
               });
               this.dialogId = dialogRef.id;
               return dialogRef.afterClosed().pipe(switchMapTo(EMPTY));
            })
         );
      },
      { dispatch: false }
   );

   dialogId: string;

   constructor(
      private actions$: Actions,
      private store: Store<CallCostRegisterState>,
      private dialogService: LcsDialogService,
      private callCostRegisterApiService: CallCostRegisterApiService
   ) {}

   private convertGetCallCostResultToLoadSuccessProps(getCallCost: GetProviderCallRate): ScheduleRegisterResults {
      return {
         endCursor: getCallCost.providerCallRatesConnection.pageInfo.endCursor,
         hasNextPage: getCallCost.providerCallRatesConnection.pageInfo.hasNextPage,
         items: getCallCost.providerCallRatesConnection.items.map(this.convertCallCostRegisterListItem.bind(this)),
         totalCount: getCallCost.providerCallRatesConnection.totalCount,
      };
   }

   private convertCallCostRegisterListItem(
      getCallCostItem: GetProviderCallRate_providerCallRatesConnection_items
   ): CallCostRegisterListItem {
      return {
         providerCallRateId: getCallCostItem?.providerCallRateId,
         providerId: getCallCostItem?.providerId,
         zone: getCallCostItem?.zone,
         rate: getCallCostItem?.rate,
         destination: getCallCostItem.destination ?? '',
         code: getCallCostItem.code ?? '',
         updateDate: getCallCostItem.updateDate,
      };
   }

   private loadNewData(
      filters: CallCostsFilterOption[],
      quickFilters: CallCostRegisterOptions | null = null,
      orderingOptions: CallCostOrderingOptions[] | null
   ) {
      if (!orderingOptions && !quickFilters) {
         orderingOptions = [{ field: 'providerId', direction: OrderingDirections.Asc }];
      }
      return this.callCostRegisterApiService.getCallCost(null, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return callCostRegisterAdapterActions.loadSuccess(
               this.convertGetCallCostResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(callCostRegisterAdapterActions.loadFailure(error));
         })
      );
   }

   private loadNewPage(
      endCursor: string | null,
      filters: CallCostsFilterOption[],
      quickFilters: CallCostRegisterOptions | null = null,
      orderingOptions: CallCostOrderingOptions[] | null
   ) {
      if (!orderingOptions && !quickFilters) {
         orderingOptions = [{ field: 'providerId', direction: OrderingDirections.Asc }];
      }
      return this.callCostRegisterApiService.getCallCost(endCursor, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return callCostRegisterAdapterActions.loadPageSuccess(
               this.convertGetCallCostResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(callCostRegisterAdapterActions.loadPageFailure(error));
         })
      );
   }
}
